import React, {useCallback, useContext, useMemo, useState} from "react";
import useTranslate from "../../modules/local/useTranslate";
import _ from "lodash";
import {NullAsDefault} from "../Shared";
import {getId, getType} from "../../apis/model/base";
import {showConfirmDialog} from "../AppLocals/factory/createConfirmDialog";
import {useLocation} from "react-use";
import createContextMenu from "../AppLocals/factory/createContextMenu";
import Modal from "../../components/Modal/Modal";
import {LoginContext} from "../../components/LoginContext";
import {BsTrash, BsXCircle} from "react-icons/bs";
import {GiCardExchange} from "react-icons/gi";
import {MdAlternateEmail} from "react-icons/md";
import {IoExitOutline} from "react-icons/io5";
import {useMemberAPI} from "./useMemberAPI";
import {MemberCreateFrom, MemberEditFrom, MemberRoleAssignFrom} from "./MemberForm";
import LoadingBlock from "../Loading/LoadingBlock";

export const MemberContextActionTypes = Object.freeze({
    ADD_MEMBER: 'add-member',
    EDIT_MEMBER: 'edit-member',
    REMOVE_MEMBER: 'remove-member',
    LEAVE_MEMBER: 'leave-member',
    ASSIGN_MEMBER_ROLE: 'assign-member-role',
    ASSIGN_MEMBER_AS_OWNER_ROLE: 'assign-as-owner-role',
    REVOKE_MEMBER_ROLE: 'revoke-member-role',
})

export const useMemberForm = ({
                                  owner
                              }) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const location = useLocation()
    const [refreshKey, setRefreshKey] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })
    const onRefresh = () => {
        setRefreshKey(Date.now())
        onCancel()
    }
    const onCancel = () => {
        setAction({})
    }

    const {
        onAddMember = NullAsDefault,
        onEditMember = NullAsDefault,
        onRemoveMember = NullAsDefault,
        onAssignMemberRole = NullAsDefault,
        onRevokeMemberRole = NullAsDefault,
        onAssignMemberAsOwner = NullAsDefault,
        onLeaveMember = NullAsDefault
    } = useMemberAPI({
        notifyListeners: {
            onMemberAdded: onRefresh,
            onMemberEdited: onRefresh,
            onMemberRemoved: onRefresh,
            onMemberAccepted: onRefresh,
            onMemberRejected: onRefresh,
            onRoleAssigned: onRefresh,
            onRoleAsOwnerAssigned: onRefresh,
            onMemberLeft: onRefresh,
        }
    })

    const handleAction = (action, params) => {
        switch (action) {
            case MemberContextActionTypes.ADD_MEMBER:
            case MemberContextActionTypes.ASSIGN_MEMBER_ROLE:
            case MemberContextActionTypes.EDIT_MEMBER:
                setAction({type: action, value: params})
                break
            case MemberContextActionTypes.REMOVE_MEMBER:
                showConfirmDialog({
                    translate: t,
                    onOk: () => {
                        onRemoveMember({}, {
                            id: getId(owner),
                            prop: getType(owner),
                            m_id: getId(params)
                        })
                    },
                })
                break
            case MemberContextActionTypes.LEAVE_MEMBER:
                showConfirmDialog({
                    translate: t,
                    onOk: () => {
                        onLeaveMember({
                            id: getId(owner),
                            prop: getType(owner)
                        }, {
                            id: getId(owner),
                            prop: getType(owner),
                            m_id: getId(params)
                        })
                    },
                })
                break
            case MemberContextActionTypes.REVOKE_MEMBER_ROLE:
                showConfirmDialog({
                    translate: t,
                    onOk: () => {
                        onRevokeMemberRole({}, {
                            id: getId(owner),
                            prop: getType(owner),
                            m_id: getId(params)
                        })
                    },
                })
                break
            case MemberContextActionTypes.ASSIGN_MEMBER_AS_OWNER_ROLE:
                showConfirmDialog({
                    translate: t,
                    onOk: () => {
                        onAssignMemberAsOwner({}, {
                            id: getId(owner),
                            prop: getType(owner),
                            m_id: getId(params)
                        })
                    },
                })
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <MdAlternateEmail size={14} className="text-green-500"/>,
                        label: 'assign role',
                        key: MemberContextActionTypes.ASSIGN_MEMBER_ROLE,
                        visible: (item) => item?.role_id === 3
                    },
                    {
                        icon: <GiCardExchange size={14} className="text-blue-500"/>,
                        label: 'assign member as owner role',
                        key: MemberContextActionTypes.ASSIGN_MEMBER_AS_OWNER_ROLE,
                        visible: (item) => item?.role_id !== 1 && item?.user?.id !== login?.id
                    },
                    {
                        icon: <BsXCircle size={14} className="text-orange-500"/>,
                        label: 'revoke role',
                        key: MemberContextActionTypes.REVOKE_MEMBER_ROLE,
                        visible: (item) => item?.role_id !== 1 && item?.role_id !== 3 && item?.user?.id !== login?.id
                    },
                    {
                        icon: <IoExitOutline size={14} className="text-red-500"/>,
                        label: 'leave',
                        key: MemberContextActionTypes.LEAVE_MEMBER,
                        visible: (item) => (item?.user?.id === login?.id && item?.role_id !== 1)
                    },
                    {
                        icon: <BsTrash size={14} className="text-red-500"/>,
                        label: 'delete',
                        key: MemberContextActionTypes.REMOVE_MEMBER,
                        visible: (item) => (item?.user?.id !== login?.id && owner?.member_role_id < item?.role_id)
                    },
                ].filter(({visible = ((e) => true)}) => visible(item)),
                (key) => {
                    handleAction(key, item)
                },
            ),
        []
    )

    const modal = useMemo(() => {
        const {id} = action?.value ?? {}
        let title
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({item: {}})
        let params = {item: action?.value}
        let modal_params = {}
        switch (action?.type) {
            case MemberContextActionTypes.ADD_MEMBER:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('add new member')}
                    </div>
                )
                params = {
                    item: action?.value,
                    onCancel,
                    onSubmit: (values) => {
                        onAddMember(_.pick(values, ['contact_email', 'name', 'contact_phone', 'role_id']), {
                            id: getId(owner),
                            prop: getType(owner),
                            u_id: values?.user_id
                        })
                    },
                }
                modal_params = {
                    width: 600,
                }
                Content = MemberCreateFrom
                break
            case MemberContextActionTypes.EDIT_MEMBER:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit member')}
                    </div>
                )
                params = {
                    item: action?.value,
                    onCancel,
                    onSubmit: (values) => {
                        onEditMember(values, {
                            id: getId(owner),
                            prop: getType(owner),
                            m_id: id
                        })
                    },
                }
                modal_params = {
                    width: 720,
                }
                Wrapper = ({children}) => children({item: action?.value})
                Content = MemberEditFrom
                break
            case MemberContextActionTypes.ASSIGN_MEMBER_ROLE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('assign member role')}
                    </div>
                )
                params = {
                    item: action?.value,
                    onCancel,
                    onSubmit: (values) => {
                        // role_id giờ nằm trong URL (EntityRoleController), không còn gửi qua body -
                        // form vẫn cho chọn owner/admin/member như cũ, nhưng chỉ admin(2)/member(3) có
                        // tác dụng (owner phải qua action riêng "assign as owner").
                        const query = {id: getId(owner), prop: getType(owner), m_id: id}
                        if (Number(values?.role_id) === 2) onAssignMemberRole({}, query)
                        else if (Number(values?.role_id) === 3) onRevokeMemberRole({}, query)
                    },
                }
                modal_params = {
                    width: 540,
                }
                Wrapper = ({children}) => children({item: action?.value})
                Content = MemberRoleAssignFrom
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                destroyOnHidden={true}
                open={action?.type}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                item={item}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action])

    return {
        modal,
        handleAction,
        refreshKey
    }
}
